import React from 'react';
import { GatsbyBrowser, navigate } from 'gatsby';
import { HelmetProvider } from 'react-helmet-async';
import { prefetchGeoEdge } from './src/contexts/franchise/locator-provider';

const isModifiedClick = (e: MouseEvent): boolean => e.metaKey || e.altKey || e.ctrlKey || e.shiftKey || e.button !== 0;

const isSameOrigin = (url: URL): boolean => url.origin === window.location.origin;

const shouldIgnoreAnchor = (a: HTMLAnchorElement, e: MouseEvent): boolean => {
    if (isModifiedClick(e)) return true;
    if (a.target && a.target.toLowerCase() === '_blank') return true;
    if (a.hasAttribute('download')) return true;
    const rel = a.getAttribute('rel')?.toLowerCase() ?? '';
    return rel.includes('external');
};

const removeRouterHash = (): void => {
    window.routerHash = undefined;
};

const scrollTo = (hash: string): void => {
    const scrollOffset = -200; // Roughly height of the sticky header - doesn't ne
    const id = decodeURIComponent(hash.replace(/^#/, ''));
    const el = document.getElementById(id);
    if (!el) return;

    const top = el.getBoundingClientRect().top + window.scrollY + scrollOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    removeRouterHash();
};

const scrollToOnElementExists = (
    hash: string,
    timeoutMs = 1000, // Ignore the update after 1000ms if the ID can't be found
): void => {
    const start = performance.now();
    const tick: FrameRequestCallback = () => {
        const id = decodeURIComponent(hash.replace(/^#/, ''));
        if (document.getElementById(id)) {
            scrollTo(hash);
            return;
        }
        if (Date.now() - start < timeoutMs) {
            requestAnimationFrame(tick);
        } else {
            removeRouterHash();
        }
    };

    tick(start);
};

const buildAnchorHandler = () => (e: MouseEvent) => {
    const target = e.target as Element | null;
    const a = target?.closest?.('a') as HTMLAnchorElement | null;
    if (!a) return;

    if (shouldIgnoreAnchor(a, e)) return;

    const hrefAttr = a.getAttribute('href');
    if (!hrefAttr) return;

    // Ignore mailto/tel/etc
    if (hrefAttr.startsWith('mailto:') || hrefAttr.startsWith('tel:')) {
        return;
    }

    let url: URL;
    try {
        url = new URL(hrefAttr, window.location.href);
    } catch {
        return;
    }

    if (!isSameOrigin(url)) return;

    const { hash } = url;
    if (!hash || hash === '#') return;

    // If this is an internal link and has a hash, run preventDefault()
    e.preventDefault();

    // Set the routerHash
    window.routerHash = hash;

    // Run navigate - this will send it to onRouteUpdate, where we can run the scrollTo if required
    navigate(`${url.pathname}${url.search}${hash}`);
};

export const onRouteUpdate: GatsbyBrowser['onRouteUpdate'] = ({ location, prevLocation }) => {
    // find if we've got a hash defined - if we do - scroll to it
    const hash = window.routerHash;

    if (hash) {
        if (prevLocation?.pathname === location.pathname) {
            // Don't wait for transition if page is the same
            scrollTo(hash);
        } else {
            // Scroll when the element is rendered/available, after a 300ms initial delay
            // Doing without the 300ms delay entirely feels janky, keeping it in
            setTimeout(() => {
                scrollToOnElementExists(hash);
            }, 300);
        }
    }
};

export const onClientEntry: GatsbyBrowser['onClientEntry'] = () => {
    window.dataLayer = window.dataLayer || [];

    const handler = buildAnchorHandler();
    document.addEventListener('click', handler, true);
    return () => {
        document.removeEventListener('click', handler, true);
    };
};

export const onInitialClientRender: GatsbyBrowser['onInitialClientRender'] = () => {
    prefetchGeoEdge();
    const { hash } = window.location;
    if (hash) {
        setTimeout(() => {
            scrollToOnElementExists(hash);
        }, 300);
    }
};

/*
 * If the pathnames are the same, and there is a router hash, don't use in-built page scroll updating
 * */
export const shouldUpdateScroll: GatsbyBrowser['shouldUpdateScroll'] = ({
    prevRouterProps,
    routerProps,
}) => !(
    prevRouterProps?.location?.pathname === routerProps?.location?.pathname
    && window.routerHash
);

export const wrapRootElement: GatsbyBrowser['wrapRootElement'] = ({
    element,
}): React.ReactElement => <HelmetProvider>{element}</HelmetProvider>;
